import {WorkflowType} from "../../utils/enums";
import {WorkflowOption} from "./types";
import {WORKFLOW_OPTIONS} from "./workflowConfig";

/**
 * Legacy / alternate spellings → canonical template key.
 * Keeps lookups working if older uppercase enum values slip through.
 */
const WORKFLOW_TYPE_ALIASES: Record<string, WorkflowType> = {
  arbitration_agreement: WorkflowType.ARBITRATION,
};

/**
 * Workflow type names the system assigns internally (e.g. as sentinel /
 * placeholder workflow types) that must never be declared as an actual
 * workflow inside a facility template, nor be manually selectable.
 * Mirrors `ReservedWorkflowNames` in go-common/content/types/workflow.go.
 */
const RESERVED_WORKFLOW_TYPES: ReadonlyArray<WorkflowType> = [
  WorkflowType.PENDING,
];

/** Case-insensitive check against RESERVED_WORKFLOW_TYPES. */
const isReservedWorkflowType = (workflowType: string): boolean =>
  (RESERVED_WORKFLOW_TYPES as ReadonlyArray<string>).includes(
    workflowType.toLowerCase(),
  );

const humanizeWorkflowType = (raw: string): string =>
  raw
    .split(/[-_]/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");

/** Canonical lowercase template key. Empty string if missing. */
const normalizeWorkflowType = (workflowType?: string): string => {
  if (!workflowType) return "";
  const normalized = workflowType.toLowerCase();
  return WORKFLOW_TYPE_ALIASES[normalized] ?? normalized;
};

/** Canonical lowercase key. Missing workflowType means legacy admission. */
export const resolveWorkflowType = (workflowType?: string | null): string =>
  normalizeWorkflowType(workflowType ?? undefined) || WorkflowType.ADMISSION;

/** True when the admission's workflow type hasn't been resolved yet (PENDING). */
export const isPendingWorkflowType = (workflowType?: string | null): boolean =>
  resolveWorkflowType(workflowType) === WorkflowType.PENDING;

/**
 * Display label. Known types use curated copy; unknown keys are humanized
 * (`vaccination_form` → `Vaccination Form`). Missing → `Admission`.
 */
export const getWorkflowLabel = (workflowType?: string | null): string => {
  const key = resolveWorkflowType(workflowType);

  if (key in WORKFLOW_OPTIONS) {
    return WORKFLOW_OPTIONS[key as WorkflowType].label;
  }

  return humanizeWorkflowType(key);
};

/**
 * Packet label for download links/titles: appends "Packet" to the workflow label,
 * unless the label already contains it (`Financial Packet` stays `Financial Packet`,
 * not `Financial Packet Packet`).
 */
export const getWorkflowPacketLabel = (workflowLabel: string): string =>
  /\bpacket\b/i.test(workflowLabel) ? workflowLabel : `${workflowLabel} Packet`;

const mapSingleWorkflowType = (rawType: string): WorkflowOption => {
  const normalized = normalizeWorkflowType(rawType);

  if (normalized in WORKFLOW_OPTIONS) {
    return WORKFLOW_OPTIONS[normalized as WorkflowType];
  }

  return {type: normalized, label: humanizeWorkflowType(normalized)};
};

/**
 * Builds workflow options from template version workflowTypes (always includes admission).
 * Reserved workflow types (RESERVED_WORKFLOW_TYPES) are excluded even if one somehow appears
 * in the template config - they must never be manually selectable.
 */
export const mapTemplateWorkflowTypesToOptions = (
  templateWorkflowTypes: ReadonlyArray<string>,
): ReadonlyArray<WorkflowOption> => {
  const types = new Set<string>([WorkflowType.ADMISSION]);

  templateWorkflowTypes.forEach(type => {
    const normalized = normalizeWorkflowType(type);
    if (
      normalized &&
      normalized !== WorkflowType.ADMISSION &&
      !isReservedWorkflowType(normalized)
    ) {
      types.add(normalized);
    }
  });

  return Array.from(types).map(mapSingleWorkflowType);
};

export const admissionOnlyWorkflowOption = (): WorkflowOption =>
  mapSingleWorkflowType(WorkflowType.ADMISSION);
